import { registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { MailEntity } from '@adornis/mail-client/mail-entity.js';
import { merge, of, type Observable } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';
import type { EnrichedMailEntity } from '../../../db/EnrichedMailEntity.js';
import { getCurrentUserID } from '../../../api/currentUserID.js';
import { CorrespondenceAnswer, CorrespondenceEntry } from '../db/Correspondence.js';
import { NotificationChannel, UserNotification } from '../db/UserNotification.js';
import { buildQuestionMailSubject } from '../question-mail-body.js';
import { stripQuotedText } from '../reply-parsing.js';
import { getNotificationCollection, type NotificationDoc } from '../server/notification-collection.js';

/**
 * A reading that started this long ago is not running any more — the process it ran in is gone.
 * Without the cap a server restart mid-call would leave that reply saying "wird ausgewertet"
 * for good, with the button that could fix it hidden behind the very state that is wrong.
 */
const READING_TIMEOUT_MS = 5 * 60 * 1000;

/**
 * The mail traffic about one request, in order.
 *
 * The point of this view is not the mails — it is the reasoning between them. A free-text
 * answer is now read by a model, and a model's reading is the one step here that cannot be
 * reconstructed after the fact. So each answer is shown with the passage it rested on, how it
 * was understood, how sure the model was, and what that did or did not change to the offer.
 * Without that, "warum steht da jetzt 6000?" has no answer.
 */

const mailDelivery = (notification: NotificationDoc) =>
  (notification.deliveries ?? []).find(delivery => delivery.channel === NotificationChannel.MAIL);

/** One entry per question mail we sent, identified by the reply token it carried. */
function buildOutgoing(notifications: NotificationDoc[], subject: string | null): CorrespondenceEntry[] {
  const batches = new Map<string, { at: Date | null; to: string | null; titles: string[] }>();

  for (const notification of notifications) {
    const delivery = mailDelivery(notification);
    if (!delivery?.replyToken || delivery.state !== 'sent') continue;
    const batch = batches.get(delivery.replyToken) ?? {
      at: delivery.lastSentAt ?? delivery.sentAt ?? null,
      to: notification.recipientEmailSnapshot ?? null,
      titles: [],
    };
    batch.titles.push(notification.title);
    batches.set(delivery.replyToken, batch);
  }

  return [...batches.entries()].map(
    ([token, batch]) =>
      new CorrespondenceEntry({
        _id: `out:${token}`,
        direction: 'out',
        at: batch.at,
        subject: buildQuestionMailSubject(subject, token),
        counterpart: batch.to,
        // Numbered the way the recipient saw them, so a reply referring to "Punkt 2" lines up.
        questions: batch.titles.map((title, index) => `${index + 1}) ${title}`),
        answers: [],
        unmatched: [],
        unread: false,
      }),
  );
}

function toAnswerView(notification: NotificationDoc): CorrespondenceAnswer {
  const answer = notification.answer;
  const reading = answer?.interpretation;
  const chosen = (notification.options ?? []).find(option => option.key === answer?.selectedOptionKey);

  return new CorrespondenceAnswer({
    _id: `ans:${notification._id}`,
    questionTitle: notification.title,
    positionNumber: notification.positionNumber,
    quote: reading?.sourceQuote ?? answer?.freeText ?? null,
    chosenOption: chosen ? `${chosen.key} — ${chosen.label}` : null,
    understoodAs: reading?.summary ?? null,
    reasoning: reading?.reasoning ?? null,
    confidence: reading?.confidence ?? null,
    outcome: answer?.outcomeSummary ?? null,
    rejections: answer?.rejections ?? [],
    status: notification.status,
    needsConfirmation: reading?.needsConfirmation ?? false,
  });
}

/**
 * One entry per reply mail — driven by the mails, not by the answers they produced.
 *
 * The difference matters: a reply that answered nothing (a reading that failed, an answer to a
 * question that was already closed) still has to appear. Building this from the answers meant
 * such a mail vanished from the view entirely, and the only conclusion left to the reader was
 * that it had never arrived.
 */
async function buildIncoming(mailID: string, notifications: NotificationDoc[]): Promise<CorrespondenceEntry[]> {
  const mails = await getRawCollection<EnrichedMailEntity>(MailEntity._collectionName);
  const replies = await mails.find<EnrichedMailEntity>({ notificationReplyFor: mailID }).toArray();
  if (!replies.length) return [];

  const answeredBy = new Map<string, NotificationDoc[]>();
  for (const notification of notifications) {
    const sourceMailID = notification.answer?.sourceMailID;
    if (!sourceMailID) continue;
    answeredBy.set(sourceMailID, [...(answeredBy.get(sourceMailID) ?? []), notification]);
  }

  return replies.map(reply => {
    const answered = answeredBy.get(reply._id) ?? [];
    const startedAt = reply.notificationReplyReadingSince?.getTime() ?? null;
    const reading = startedAt !== null && Date.now() - startedAt < READING_TIMEOUT_MS;
    return new CorrespondenceEntry({
      _id: `in:${reply._id}`,
      direction: 'in',
      at: reply.receivedAt ?? null,
      subject: reply.subject ?? null,
      counterpart: reply.from?.[0]?.address ?? null,
      questions: [],
      // The quoted question mail comes back with every reply; showing it would bury the two
      // lines the person actually wrote under the block they were answering.
      bodyText: stripQuotedText(reply.textContent ?? '') || null,
      answers: answered.map(toAnswerView),
      unmatched: reply.notificationReplyUnmatched ?? [],
      reading,
      // Nothing came of this letter. The view offers to read it again rather than leaving the
      // reader to guess whether it was ignored on purpose — but only once the reading is over,
      // otherwise every arriving answer is declared a failure for as long as it takes to read.
      unread: answered.length === 0 && !reading,
      replyMailID: reply._id,
    });
  });
}

/** The whole exchange of one request, newest last. */
async function buildCorrespondence(mailID: string): Promise<CorrespondenceEntry[]> {
  const collection = await getNotificationCollection();
  const notifications = await collection.find({ mailID }, { sort: { createdAt: 1 } }).toArray();
  if (!notifications.length) return [];

  const mails = await getRawCollection<MailEntity>(MailEntity._collectionName);
  const request = await mails.findOne<MailEntity>({ _id: mailID }, { projection: { subject: 1 } });

  const entries = [
    ...buildOutgoing(notifications, request?.subject ?? null),
    ...(await buildIncoming(mailID, notifications)),
  ];
  return entries.sort((a, b) => (a.at?.getTime() ?? 0) - (b.at?.getTime() ?? 0));
}

/**
 * Live rather than read-once, because the two moments this view exists for both happen while
 * someone is already looking at it: an answer arriving, and that answer having been read.
 *
 * Watching the two sources it is assembled from — the questions of this request and the reply
 * mails belonging to it — rather than polling. Both filters are narrow, so a busy mailbox does
 * not wake every open request. The view is rebuilt on each signal instead of patched: it is a
 * handful of documents, and a second way of deriving it is a second way for it to be wrong.
 */
export const subscribeRequestCorrespondence = registerSubscription({
  type: () => [CorrespondenceEntry],
  operationName: 'subscribeRequestCorrespondence',
  resolve: (mailID: string) => {
    return (_gqlFields: BaseQLSelectionSet<CorrespondenceEntry>): Observable<CorrespondenceEntry[]> => {
      if (!getCurrentUserID()) return of([]);

      return merge(
        watchQuery<UserNotification>(UserNotification._collectionName, { mailID }),
        watchQuery<EnrichedMailEntity>(MailEntity._collectionName, { notificationReplyFor: mailID }),
      ).pipe(
        // Both watches emit their initial read at once, and one reply being read writes to both
        // collections in quick succession — without this the view would rebuild several times
        // over for a single event.
        debounceTime(100),
        switchMap(() => buildCorrespondence(mailID)),
      );
    };
  },
  params: [{ name: 'mailID', type: () => String }],
});
